'use client';

import type {FormEvent} from 'react';
import {useRouter} from 'next/navigation';
import * as Form from '@radix-ui/react-form';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import TextInput, {getValuesFromFormEvent} from './form/TextInput';
import ResponsiveModal from './ResponsiveModal';
import fetcher from 'src/utils/fetcher';
import BaseButton from './BaseButton';

type EditableValues = {
  name: string;
  description: string;
};

type Props = {
  onClose: () => void;
  isOpen: boolean;
  list: HumboList;
  children?: React.ReactNode;
};

const EditListModal = ({onClose, isOpen, list, children}: Props) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const {mutateAsync: updateList, isPending} = useMutation({
    mutationFn: (values: EditableValues) =>
      fetcher({
        url: `/api/lists/${list.user?.nameId}/${list.nameId}`,
        options: {method: 'PUT'},
        body: values
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['list']
      });
      queryClient.invalidateQueries({
        queryKey: ['myLists']
      });
      router.refresh();
    }
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    const values = getValuesFromFormEvent(event, ['name', 'description']) as EditableValues;

    event.preventDefault();
    updateList(values);

    onClose();
  };

  return (
    <>
      <ResponsiveModal isOpen={isOpen} trigger={children} title="Edit the list" onClose={onClose}>
        <Form.Root className="flex flex-col mt-6" onSubmit={onSubmit}>
          <TextInput
            defaultValue={list.name}
            name="name"
            label="Name"
            required
            autoFocus
            validations={[{match: 'valueMissing', children: 'What should be the name of your list?'}]}
          />

          <TextInput name="description" label="Description" defaultValue={list.description} textarea />

          <div className="flex mt-2 gap-4">
            <BaseButton
              type="button"
              onPress={onClose}
              label="Cancel"
              buttonVariant="secondary"
              className="grow"
            />

            <Form.Submit asChild disabled={isPending}>
              <BaseButton label="Save list" isLoading={isPending} buttonVariant="primary" className="grow" />
            </Form.Submit>
          </div>
        </Form.Root>
      </ResponsiveModal>
    </>
  );
};

export default EditListModal;
